import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import React from "react";
import { useTranslation } from "react-i18next";

const AccountingPageHeader = ({
  title,
  primaryAction,
  secondaryAction,
  dropDownAction,
  additionalActions,
}) => {
  const { t } = useTranslation();
  // Add buttons here open modals. On a write-blocked business the modal itself
  // shows the "subscribe to unlock" card (WriteBlockGate), so the triggers stay
  // enabled — the block is surfaced consistently inside the modal, not by a
  // silently-disabled button. Non-modal on-page forms disable their own submit.

  return (
    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {t(title)}
          </h2>
        </div>
        {(primaryAction ||
          secondaryAction ||
          dropDownAction ||
          additionalActions) && (
          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
            {additionalActions && (
              <div className="w-full sm:w-auto">{additionalActions}</div>
            )}
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                {secondaryAction.icon && (
                  <span className="mr-2">{secondaryAction.icon}</span>
                )}
                {t(secondaryAction.label)}
              </button>
            )}
            {primaryAction && (
              <button
                type="button"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                {primaryAction.icon && (
                  <span className="mr-2">{primaryAction.icon}</span>
                )}
                {t(primaryAction.label)}
              </button>
            )}
            {dropDownAction && (
              <div className="inline-flex rounded-md shadow-sm w-full sm:w-auto">
                <button
                  type="button"
                  disabled={dropDownAction.disabled}
                  className="relative inline-flex items-center justify-center rounded-l-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white ring-1 ring-inset focus:ring-blue-500 focus:z-10 flex-1 sm:flex-none"
                  onClick={dropDownAction.onClick}
                >
                  {dropDownAction.disabled ? (
                    <svg
                      className="animate-spin mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  ) : (
                    dropDownAction.icon && (
                      <span className="mr-2">{dropDownAction.icon}</span>
                    )
                  )}
                  {dropDownAction.label}
                </button>
                <Menu as="div" className="relative -ml-px block">
                  <MenuButton
                    disabled={dropDownAction.disabled}
                    className="relative inline-flex items-center rounded-r-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed px-2 py-2 text-white ring-1 ring-inset focus:ring-blue-500 focus:z-10"
                  >
                    <span className="sr-only">Open options</span>
                    <ChevronDownIcon aria-hidden="true" className="h-6 w-6" />
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <div className="py-1">
                      {dropDownAction.items.map((item) => (
                        <MenuItem key={item.name}>
                          <button
                            onClick={item.onClick}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                          >
                            {item.name}
                          </button>
                        </MenuItem>
                      ))}
                    </div>
                  </MenuItems>
                </Menu>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingPageHeader;
